import { CONFIGS } from '@buildwise/libs/configurations';
import useAddPostForm from '@buildwise/pages/addPost/hooks/useAddPostForm';
import {
  ArticleFieldsType,
  ArticleSourceType,
  ArticleType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import PostDetailsForm from 'components/modals/addPostModal/PostDetailsForm';
import { theme } from 'config/theme';
import React, { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import ArrowLeftLineIcon from 'remixicon-react/ArrowLeftLineIcon';
import { useHelpCenterArticlesQuery } from '../queries/useHelpCenterArticlesQuery';
import { checkIsPlaylistArticle } from '../helpers';

type AddHelpCenterArticleModalPros = {
  openArticleModal: boolean;
  setOpenArticleModal: React.Dispatch<React.SetStateAction<boolean>>;
  article?: ArticleType;
};

function AddHelpCenterArticleModal({
  openArticleModal,
  setOpenArticleModal,
  article,
}: AddHelpCenterArticleModalPros) {
  const { refetch, playlists, updateHelpCenterArticlesCache } = useHelpCenterArticlesQuery();
  const isEditing = useMemo(() => !!article, [article]);
  const handleClose = () => {
    setOpenArticleModal(false);
  };

  const updateCache = (article: ArticleType) => {
    const isPlaylistArticle = checkIsPlaylistArticle(playlists, article.id);
    if (isPlaylistArticle) {
      refetch();
    } else {
      updateHelpCenterArticlesCache({ action: 'update', updatedArticle: article });
    }
  };
  const { articleFields, loadingFiles, postForm, isSubmitting, onDrop, onSubmit } = useAddPostForm({
    isEditing,
    data: article,
    setOpen: handleClose,
    articleType: ArticleSourceType.HELP_CENTER,
    articleFieldsType: ArticleFieldsType.HELP_CENTER,
    onAddArticleCallback: ({ article }) => {
      updateCache(article);
    },
    updateArticleCallback: ({ updatedArticle }) => {
      updateCache(updatedArticle);
    },
  });

  return (
    <Dialog
      open={openArticleModal}
      fullWidth
      fullScreen={true}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      disableEnforceFocus={true}
      disableAutoFocus={true}
      sx={{
        WebkitOverflowScrolling: 'touch',
        '& .MuiPaper-root': {
          minHeight: '60%',
          transition: 'all 0.2s',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          p: '16px 16px',
          color: theme.palette.custom.tuna,
          backgroundColor: theme.palette.custom.white,
          borderBottom: `1px solid ${theme.palette.custom.blueChalk}`,
          position: 'sticky',
          top: 0,
          zIndex: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleClose}>
            <ArrowLeftLineIcon size={24} color={theme.palette.custom.tuna} />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: 20, ml: 4 }}>
            {isEditing ? 'Edit' : 'New'} Help Article
          </Typography>
        </Box>
        <Button variant="contained" onClick={onSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
          ) : (
            'Save'
          )}
        </Button>
      </Box>
      <FormProvider {...postForm}>
        <Box sx={{ width: '80%', mx: 'auto', mt: 4 }}>
          <PostDetailsForm
            article={article}
            articleType={ArticleSourceType.HELP_CENTER}
            isEditing={isEditing}
            isFullScreen
            loadingFiles={loadingFiles}
            onDrop={onDrop}
            articleFields={articleFields}
            configName={CONFIGS.HELP_CENTER_INPUT_FIELDS.configName}
            getFieldWidth={() => '100%'}
          />
        </Box>
      </FormProvider>
    </Dialog>
  );
}

export default AddHelpCenterArticleModal;
